import { Form } from "../../core/Form";
import { EmailVerificationModal } from "../../components/EmailVerificationModal";
import * as Modal from "../../utils/Modal";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^0\d{1,2}-?\d{3,4}-?\d{4}$/;

function wireFileNamePreviews(form: HTMLFormElement): void {
    form.querySelectorAll<HTMLInputElement>("[data-file-input]").forEach((input) => {
        const update = (): void => {
            const wrap = input.closest(".file");
            const label = wrap?.querySelector<HTMLElement>("[data-file-name]");
            if (!label) return;
            const file = input.files?.[0];
            label.textContent = file ? file.name : "선택된 파일이 없습니다.";
        };
        input.addEventListener("change", update);
        update();
    });
}

function pickFirstErrorMessage(data: Record<string, unknown> | undefined, fallback: string): string {
    if (!data) return fallback;
    const message = typeof data.message === "string" ? data.message : "";
    const errors = data.errors as Record<string, string[]> | null | undefined;
    if (errors && typeof errors === "object") {
        for (const key of Object.keys(errors)) {
            const arr = errors[key];
            if (Array.isArray(arr) && arr.length > 0) {
                return message || arr[0];
            }
        }
    }
    return message || fallback;
}

export const ContactController = {
    diagnosis(): void {},

    inquiry(): void {
        const form = document.querySelector<HTMLFormElement>("[data-inquiry-form]");
        if (!form) return;

        wireFileNamePreviews(form);

        const modal = new EmailVerificationModal({
            form,
            emailInputSelector: "#inquiry_email",
            verifiedHiddenSelector: "[data-email-verified]",
            verifiedLabelSelector: "[data-email-verified-label]",
            openButtonSelector: "[data-email-verify-open]",
            purpose: "contact_inquiry",
        });
        modal.init();

        new Form(form, {
            submitButtonSelector: 'button[type="submit"]',
            loaderSelector: "[data-form-loader]",
            submittingText: "전송 중...",
            validate: (f) => {
                const get = (sel: string) =>
                    f.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(sel);
                const checks: Array<[string, string, () => boolean]> = [
                    ["#inquiry_name", "이름을 입력해 주세요.", () => (get("#inquiry_name")?.value ?? "").trim() !== ""],
                    ["#inquiry_email", "이메일을 입력해 주세요.", () => (get("#inquiry_email")?.value ?? "").trim() !== ""],
                    ["#inquiry_email", "정상적인 이메일을 입력해 주세요.", () => EMAIL_PATTERN.test((get("#inquiry_email")?.value ?? "").trim())],
                    ["#inquiry_email", "이메일 인증을 완료해 주세요.", () => modal.isVerified()],
                    ["#inquiry_phone", "연락처를 입력해 주세요.", () => (get("#inquiry_phone")?.value ?? "").trim() !== ""],
                    ["#inquiry_phone", "정상적인 연락처를 입력해 주세요.", () => PHONE_PATTERN.test((get("#inquiry_phone")?.value ?? "").trim())],
                    ["#inquiry_type", "유형을 선택해 주세요.", () => (get("#inquiry_type")?.value ?? "").trim() !== ""],
                    ["#inquiry_store", "매장명을 입력해 주세요.", () => (get("#inquiry_store")?.value ?? "").trim() !== ""],
                    ['input[name="reply_preference"]:checked', "답변 희망 여부를 선택해 주세요.", () => !!f.querySelector('input[name="reply_preference"]:checked')],
                    ["#inquiry_title", "제목을 입력해 주세요.", () => (get("#inquiry_title")?.value ?? "").trim() !== ""],
                    ["#inquiry_contents", "문의 내용을 입력해 주세요.", () => (get("#inquiry_contents")?.value ?? "").trim() !== ""],
                    ['input[name="privacy_agree"]:checked', "개인정보 수집약관에 동의해 주세요.", () => !!f.querySelector('input[name="privacy_agree"]:checked')],
                ];
                for (const [selector, msg, ok] of checks) {
                    if (!ok()) {
                        if (selector === '#inquiry_email' && msg.includes("인증")) {
                            // 인증 모달을 자동으로 띄워준다
                            (f.querySelector<HTMLButtonElement>("[data-email-verify-open]"))?.click();
                        } else {
                            const focusSel = selector.replace(":checked", "");
                            (f.querySelector<HTMLElement>(focusSel))?.focus();
                        }
                        return msg;
                    }
                }
                return null;
            },
            onSuccess: async (data) => {
                const msg = (data?.message as string) ?? "문의가 접수되었습니다.";
                await Modal.alert(msg, "문의 접수 완료", { onOk: () => location.reload() });
            },
            onFail: (message, data) => {
                window.alert(pickFirstErrorMessage(data, message || "접수 중 오류가 발생했습니다."));
            },
            requestFailedMessage: "네트워크 오류로 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.",
        }).init();
    },

    history(): void {
        const form = document.querySelector<HTMLFormElement>("[data-history-form]");
        if (!form) return;
        // 인증 모달은 EmailVerificationModal 래퍼, 폼 자체는 GET native submit 으로 유지.
        // 인증 완료 시 자동으로 form.submit() — 사용자가 따로 "확인하기" 버튼을 누르지 않아도 됨.
        new EmailVerificationModal({
            form,
            emailInputSelector: "#history_email",
            verifiedHiddenSelector: "[data-email-verified]",
            verifiedLabelSelector: "[data-email-verified-label]",
            openButtonSelector: "[data-email-verify-open]",
            purpose: "contact_history",
            onVerified: () => {
                // 약관 동의도 자동 처리 — 인증을 한 시점에 사용자가 약관 안 누른 상태일 수 있음
                const privacy = form.querySelector<HTMLInputElement>('input[name="privacy_agree"]');
                if (privacy && !privacy.checked) privacy.checked = true;
                form.submit();
            },
        }).init();
    },

    submit(): void {},
};
